import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { SummaryCards } from '@/components/reports/SummaryCards';
import { PaymentBreakdownChart } from '@/components/reports/PaymentBreakdownChart';
import { TopProductsTable } from '@/components/reports/TopProductsTable';
import { CashierBreakdownTable } from '@/components/reports/CashierBreakdownTable';
import { StockMovementTable } from '@/components/reports/StockMovementTable';
import { EventBrowser } from '@/components/reports/EventBrowser';
import { ExportMenu } from '@/components/reports/ExportMenu';
import {
  useEventsForReport,
  useEventSummary,
} from '@/hooks/useReports';
import { formatIDR, formatDate } from '@/lib/format';

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('event');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(eventIdFromUrl);

  const { data: events, isLoading: loadingEvents } = useEventsForReport();
  const { data: summary, isLoading: loadingSummary } = useEventSummary(selectedEventId);

  const selectedEvent = events?.find((e) => e.id === selectedEventId) ?? null;
  const isClosed = selectedEvent?.status === 'closed';

  const handleEventChange = (id: string) => {
    setSelectedEventId(id || null);
    if (id) {
      setSearchParams({ event: id });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <PageHeader
        overline="Per-event reports"
        title="Reports"
        description="Ringkasan penjualan per event, breakdown payment, top produk, performa kasir, dan stock movement."
        actions={
          selectedEvent && summary ? (
            <ExportMenu
              event={{
                id: selectedEvent.id,
                name: selectedEvent.name,
                code: selectedEvent.code,
                startAt: selectedEvent.startAt,
                endAt: selectedEvent.endAt,
              }}
              summary={summary}
            />
          ) : undefined
        }
      />

      {/* ── Event browser ────────────────────────────────────────────────
          Primary way to pick an event. Visual list of all events (active
          first, then closed history) so users can scan and click instead
          of scrolling a long dropdown. Persists selection in URL.
      */}
      {loadingEvents ? (
        <LoadingState message="Loading events…" />
      ) : (
        <div className="mb-6">
          <EventBrowser
            events={events ?? []}
            selectedId={selectedEventId}
            onSelect={handleEventChange}
          />
        </div>
      )}

      {/* ── Quick-jump dropdown (secondary) ─────────────────────────────
          Kept for keyboard users / power users who already know the
          event code. Stays in sync with the browser above.
      */}
      <div className="mb-6 flex items-end gap-3 max-w-md">
        <Select
          label="Quick jump"
          value={selectedEventId ?? ''}
          onChange={(value) => handleEventChange(value)}
        >
          <option value="">— Pilih event —</option>
          {events?.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} ({e.code}) {e.status === 'closed' ? '✓' : '●'}
            </option>
          ))}
        </Select>
      </div>

      {!selectedEventId ? (
        <EmptyState
          icon={<BarChart3 size={24} strokeWidth={1.5} />}
          title="Pilih event untuk lihat report"
          description="Pilih event dari daftar di atas (atau quick jump) untuk lihat ringkasan, breakdown, dan export CSV."
        />
      ) : loadingSummary ? (
        <LoadingState message="Menghitung ringkasan event…" />
      ) : !summary ? (
        <Card>
          <p className="text-body text-ink-subtle">Tidak ada data untuk event ini.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Header with event info */}
          <Card>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-overline uppercase text-ink-subtle font-medium">Event</p>
                <h2 className="text-section font-display font-semibold text-ink mt-1 truncate">
                  {selectedEvent?.name}
                </h2>
                <p className="text-caption text-ink-subtle mt-0.5 font-mono">
                  {selectedEvent?.code}
                </p>
              </div>
              <div className="text-right text-small text-ink-subtle shrink-0">
                <p>
                  {formatDate(selectedEvent?.startAt ?? 0)}
                  {selectedEvent?.endAt ? ` → ${formatDate(selectedEvent.endAt)}` : ''}
                </p>
                <p className="text-caption">
                  {isClosed ? 'Event closed' : 'Event aktif'}
                </p>
              </div>
            </div>
          </Card>

          <SummaryCards summary={summary} />

          <div className="grid gap-4 lg:grid-cols-2">
            <PaymentBreakdownChart breakdown={summary.paymentMethodBreakdown} />
            <CashierBreakdownTable cashiers={summary.cashierBreakdown} />
          </div>

          <TopProductsTable products={summary.topProducts} />

          {/* Stock movement — per-event qty in / sold / returned / loss.
              Works for both active and closed events. The only place to
              see stock once an event is closed. */}
          <StockMovementTable eventId={selectedEventId} isClosed={isClosed} />

          {/* Discounts & Tax summary */}
          {(summary.totalDiscount > 0 || summary.totalTax > 0) && (
            <Card>
              <h3 className="text-subhead font-display font-semibold text-ink mb-3">
                Discount &amp; tax impact
              </h3>
              <dl className="grid sm:grid-cols-3 gap-4">
                <div>
                  <dt className="text-overline uppercase text-ink-subtle">Total discount</dt>
                  <dd className="text-subhead font-display font-semibold text-danger mt-1">
                    − {formatIDR(summary.totalDiscount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-overline uppercase text-ink-subtle">Total PPN</dt>
                  <dd className="text-subhead font-display font-semibold text-ink mt-1">
                    {formatIDR(summary.totalTax)}
                  </dd>
                </div>
                <div>
                  <dt className="text-overline uppercase text-ink-subtle">Net (after returns)</dt>
                  <dd className="text-subhead font-display font-semibold text-ink mt-1">
                    {formatIDR(summary.totalSales - summary.returnsTotal)}
                  </dd>
                </div>
              </dl>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
